import type { Ref } from 'react';
import { Link, type LinkProps } from 'react-router-dom';

import { GLOBAL_SEARCH_ENTITY_ICON } from 'components/global-search/entityDisplay';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import { cn } from 'utils/cn';
import { ENTITY_TYPE_FAMILY, familyTileClass, RECORD_KIND_FAMILY } from 'utils/families';
import { pathForResult, type RecordKind, UNSEARCHED_RECORD_SEGMENT } from 'utils/globalSearchRoutes';
import { PIN_KINDS } from 'utils/pins';

/** A contact's label that starts with its party number, as the AI writes a party's display name: `[O002] Orion`. */
const NUMBERED = /^\[([^\]]+)\]\s*(.*)$/s;

export type RecordChipProps = Omit<LinkProps, 'to' | 'id' | 'title' | 'children'> & {
    entityType: RecordKind;
    id: number;
    /** The record's name. A contact's may start with its party number in brackets, and the chip shows it so. */
    label: string;
    /**
     * A contact's party number, for a label without one; given, the label is the name as it stands. The caller leaves
     * it out where the firm shows none.
     */
    partyNumber?: string | null;
    /** A query on the record's page, such as the one that opens an invoice's comments on an entry. */
    search?: string;
    /** Records where the user came from as `returnTo`, as a `LinkWithOrigin` does. */
    withOrigin?: boolean;
    /** Leaves the whole label out of the native title, for a caller that shows a cut label itself. */
    hideTitle?: boolean;
    /**
     * Grows the target to `hit-area`'s, past the chip's own box. Off where chips stack or wrap with no gap for it, as
     * in place of a record link: a later chip's area would take the clicks on the bottom of the one above it.
     */
    hitArea?: boolean;
    /** Wraps a long label onto more lines rather than cutting it: an identifier, such as a case number alone. */
    wrapLabel?: boolean;
    className?: string;
    ref?: Ref<HTMLAnchorElement>;
};

/** A record's glyph and family: search's for its families, a pin's for a task and a training, which search lacks. */
function lookOf(entityType: RecordKind) {
    return entityType === 'TASK' || entityType === 'TRAINING'
        ? {
              Icon: PIN_KINDS[UNSEARCHED_RECORD_SEGMENT[entityType]].icon,
              family: RECORD_KIND_FAMILY[UNSEARCHED_RECORD_SEGMENT[entityType]],
          }
        : { Icon: GLOBAL_SEARCH_ENTITY_ICON[entityType], family: ENTITY_TYPE_FAMILY[entityType] };
}

/**
 * A record named in an AI surface, in either design, and in the new design wherever a link names one (`RecordLink`,
 * `PartyName`): its family's glyph and colours, as the navigation, Pinned and search draw it, on a plain link to the
 * record. A record with no family (a timecard, a task) takes the neutral pair; an invoice number is set in mono. Being
 * a plain record link with no `aria-haspopup`, it gets the app's peek card from `LinkPeekHost`, for every kind search
 * serves. A contact shows its number before the name, as the app writes a party everywhere; the number never truncates,
 * a long name does, and the whole label stays in the title. The name stays underlined while the link's peek card is up.
 * The link's own props (a target, a click handler, a state) pass through, so a chip opens its record as the link it
 * replaces did.
 */
export function RecordChip({
    entityType,
    id,
    label,
    partyNumber,
    search,
    withOrigin,
    hideTitle,
    hitArea = true,
    wrapLabel,
    className,
    ...linkProps
}: RecordChipProps) {
    const { Icon, family } = lookOf(entityType);
    const Anchor = withOrigin ? LinkWithOrigin : Link;
    const givenNumber = entityType === 'PARTY' ? partyNumber?.trim() : undefined;
    // Read off the label only without a given number: a contact's own name may start with brackets.
    const numbered = entityType === 'PARTY' && !givenNumber ? NUMBERED.exec(label) : null;
    const number = numbered ? numbered[1] : givenNumber;
    const name = numbered ? numbered[2] : label;
    const fullLabel = number ? `[${number}] ${name}` : name;
    return (
        <Anchor
            {...linkProps}
            to={pathForResult(entityType, id) + (search ?? '')}
            title={hideTitle ? undefined : fullLabel}
            // The number and the name are two flex items, and a flex box lays out no space between them.
            aria-label={number ? fullLabel : undefined}
            // `w-fit`: a column host (`flex-col`, a grid) would otherwise stretch the chip across the cell. `min-w-0`:
            // a flex host's `shrink-0` sibling would otherwise be pushed past its edge rather than the name cut.
            className={cn(
                hitArea && 'hit-area',
                'relative inline-flex w-fit max-w-full min-w-0 items-center gap-1 rounded-md px-1.5 font-medium no-underline hover:underline data-peek-open:underline',
                familyTileClass(family),
                entityType === 'INVOICE' && 'font-mono',
                className
            )}
        >
            <Icon className="size-3.5 shrink-0" />
            {number ? <span className="shrink-0 tabular-nums">[{number}]</span> : null}
            {/* Drawn as the gap; there for the text a table download or a copy reads. */}
            {number ? ' ' : null}
            <span className={cn('min-w-0', wrapLabel ? 'break-words whitespace-normal' : 'truncate')}>{name}</span>
        </Anchor>
    );
}
